// Tier-A generic field extraction: vendor, invoice number, invoice date, total.
// Heuristics over reconstructed lines; per-manufacturer profiles (Tier B) refine
// these later as data, not code. Abstain-don't-invent: a field we can't find
// comes back low-confidence or null, never fabricated.

import type { Field, Line } from "./types";
import { field } from "./types";

const KNOWN_MAKERS =
  /\b(sonova|phonak|resound|gn\s?hearing|oticon|demant|widex|starkey|signia|unitron|bernafon|wsa|beltone|interton)\b/i;

export const MONEY_RE = /\(?-?\$?\s?\d{1,3}(?:[, ]\d{3})*\.\d{2}\)?/g;

export function parseMoney(s: string): number | null {
  const cleaned = s.replace(/[\s$,]/g, "");
  const paren = /^\(.*\)$/.test(cleaned);
  const n = parseFloat(cleaned.replace(/[()]/g, ""));
  if (Number.isNaN(n)) return null;
  return paren ? -n : n;
}

export function findMaker(lines: Line[]): Field<string> {
  const head = lines.slice(0, 8);
  for (const l of head) {
    const m = l.text.match(KNOWN_MAKERS);
    if (m) {
      // Prefer the whole org line, cleaned of trailing parentheticals
      const org = l.text.replace(/\s*\(.*\)\s*$/, "").replace(/\s{2,}/g, " ").trim();
      return field(org || m[0], 0.9, "text");
    }
  }
  const first = head.find(l => l.text.trim().length >= 3);
  return field(first ? first.text.replace(/\s{2,}/g, " ").trim() : "Manufacturer", 0.55, "text");
}

const NO_LABEL = /(invoice|facture)\s*(no|n[oº°]|#|number|num[eé]ro)?\s*[.:#]?/i;

export function findInvoiceNo(lines: Line[]): Field<string> {
  for (const l of lines) {
    if (!NO_LABEL.test(l.text)) continue;
    const after = l.text.replace(NO_LABEL, " ");
    const m = after.match(/ \s*([A-Z0-9][A-Z0-9-]{4,})/i);
    if (m && m[1] && !/^\d{4}-\d{2}-\d{2}$/.test(m[1])) return field(m[1], 0.95, "text");
  }
  // fallback: any INV/PH/RS-style code anywhere
  for (const l of lines) {
    const m = l.text.match(/\b([A-Z]{2,4}-\d{4,}(?:-\d+)?)\b/);
    if (m && m[1]) return field(m[1], 0.6, "text");
  }
  return field("", 0.2, "text");
}

const MONTHS: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7,
  august: 8, september: 9, october: 10, november: 11, december: 12,
};

function normalizeDate(raw: string): string | null {
  let m = raw.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = raw.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})\b/);
  if (m && m[1]) {
    const mo = MONTHS[m[1].toLowerCase()];
    if (mo) return `${m[3]}-${String(mo).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
  }
  m = raw.match(/\b(\d{1,2})[/](\d{1,2})[/](\d{4})\b/);
  if (m && m[1] && m[2] && m[3]) {
    // ambiguous d/m vs m/d: if first part > 12 it must be a day
    const a = parseInt(m[1], 10), b = parseInt(m[2], 10);
    const [day, mo] = a > 12 ? [a, b] : [b, a]; // default to m/d (NA invoices)
    if (mo >= 1 && mo <= 12) return `${m[3]}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return null;
}

export function findInvoiceDate(lines: Line[]): Field<string> {
  for (const l of lines) {
    if (!/date/i.test(l.text)) continue;
    const iso = normalizeDate(l.text);
    if (iso) return field(iso, 0.95, "text");
  }
  for (const l of lines) {
    const iso = normalizeDate(l.text);
    if (iso) return field(iso, 0.6, "text");
  }
  return field("", 0.2, "text");
}

export interface Totals { total: Field<number> | null; subtotal: number | null; tax: number | null }

export function findTotals(lines: Line[]): Totals {
  let total: Field<number> | null = null;
  let subtotal: number | null = null;
  let tax: number | null = null;

  for (const l of lines) {
    const t = l.text;
    const moneys = [...t.matchAll(MONEY_RE)].map(m => parseMoney(m[0])).filter((n): n is number => n !== null);
    const last = moneys.length ? moneys[moneys.length - 1] : undefined;
    if (last === undefined) continue;
    if (/sub\s?-?total|sous-total/i.test(t)) { subtotal = last; continue; }
    if (/\b(gst|hst|tps|tvh|tax)\b/i.test(t)) { tax = last; continue; }
    if (/\btotal\b/i.test(t)) total = field(last, 0.8, "text");
  }
  // arithmetic cross-check lifts confidence
  if (total && subtotal !== null && tax !== null && Math.abs(subtotal + tax - total.value) <= 0.02) {
    total = { ...total, confidence: 0.97 };
  }
  return { total, subtotal, tax };
}
